import React from "react"
import styled from "styled-components"

import { Modal, ModalProps } from "components/modal"
import { Box, Flex, FlexColumn } from "components/box"
import { H3, EyebrowSmall, Paragraph, PlainLink } from "components/text"
import { PrimaryButton } from "components/buttons"
import { RoundTextIcon } from "components/page-elements"
import { Separator } from "components/separator"

import { formatPrice, displayedProductName, drugFactsHref } from "shared/products/helpers"
import { FontConfigurations } from "theme"
import { DrugTypeEnum, DosageTypeEnum } from "typings/types"

const GeneralInformationContainer = styled(Box)`
  padding: ${props => props.theme.space[5]} ${props => props.theme.space[5]} ${props => props.theme.space[4]};
  border-bottom: 6px solid ${props => props.theme.colors.iceBlue};
`

const PlanSelectionContainer = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  padding: ${props => props.theme.space[5]};
`

const ButtonContainer = styled(Flex).attrs({
  py: 3,
  mt: 2
})`
  width: 100%;
  justify-content: center;
  border-top: 2px solid ${props => props.theme.colors.iceBlue};
`

const StyledEyebrow = styled(EyebrowSmall).attrs({
  ...FontConfigurations.eyebrowSm,
  fontSize: "0.75rem",
  color: "coolGrey"
})``

const Container = styled(FlexColumn)`
  @media screen and (min-width: ${props => props.theme.breakpoints.sm}px) {
    max-width: 375px;
  }
`

type MedicationDetailsModalProps = {
  drugType: DrugTypeEnum
  dosageType: Maybe<DosageTypeEnum>
  genericName: Maybe<string>
  description: Maybe<string>
  imageSrc: string
} & ModalProps

export const MedicationDetailsModal: React.FC<MedicationDetailsModalProps> = ({
  isOpen,
  setIsOpen,
  drugType,
  dosageType,
  genericName,
  description,
  imageSrc
}) => (
  <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
    <Container>
      <img src={imageSrc} />
      <GeneralInformationContainer>
        <H3 fontSize="2rem">{displayedProductName(drugType, dosageType)}</H3>
        <Flex mt={2} mb={4}>
          <StyledEyebrow mr={3}>{genericName}</StyledEyebrow>
          <StyledEyebrow forwardedAs={PlainLink} href={drugFactsHref(drugType, dosageType)}>
            DRUG FACTS
          </StyledEyebrow>
        </Flex>
        <Paragraph fontSize="1rem" mb={4}>
          {description}
        </Paragraph>
      </GeneralInformationContainer>
      <PlanSelectionContainer>
        <Paragraph fontSize="0.875rem" bold>
          PLAN
        </Paragraph>
        <RoundTextIcon bg="paleAqua">REQUIRED</RoundTextIcon>
      </PlanSelectionContainer>
      <Separator borderColor="iceBlue" width="87%" />
      <ButtonContainer>
        <PrimaryButton sizing="lg" noShadow>
          Add to Plan | {formatPrice(40)}
        </PrimaryButton>
      </ButtonContainer>
    </Container>
  </Modal>
)
